import type { SiteData, BracketData } from "../types";
import { flipArray, itemLast } from "./utils";

function setSeeds64(
  siteData: SiteData,
  brackets: BracketData,
  type: "winners" | "losers",
): SiteData {
  let seedCount = 1;
  const arr = brackets[type];
  for (let x = 0; x < arr.length; x += 2) {
    const songValues = [arr[x]!, arr[x + 1]!];
    siteData.bracketsModeSettings.gameSeedSetup.activeSeeds[
      "seed" + seedCount
    ] = songValues;
    seedCount += 1;
  }
  siteData.bracketsModeSettings.current2Seeds =
    siteData.bracketsModeSettings.gameSeedSetup.activeSeeds[
      "seed" + siteData.bracketsModeSettings.currentSeed
    ]!;
  return siteData;
}

export interface BracketScenarioResult {
  siteData: SiteData;
  brackets: BracketData;
  isFinal: boolean;
}

export function bracketScenario64(
  inputSiteData: SiteData,
  inputBrackets: BracketData,
  winner: number,
  loser: number,
): BracketScenarioResult {
  const siteData = structuredClone(inputSiteData);
  const brackets = structuredClone(inputBrackets);

  const { currentSeed, gameResults, currentRound } =
    siteData.bracketsModeSettings;

  // Round set definitions (126 total rounds)
  const winnersRoundsSet1 = Array.from({ length: 32 }, (_, i) => i + 1);        // 1-32
  const losersRoundsSet1 = Array.from({ length: 16 }, (_, i) => i + 33);        // 33-48
  const winnersRoundSet2 = Array.from({ length: 16 }, (_, i) => i + 49);        // 49-64
  const losersRoundsSet2 = Array.from({ length: 24 }, (_, i) => i + 65);        // 65-88
  const winnersRoundSet3 = Array.from({ length: 8 }, (_, i) => i + 89);         // 89-96
  const losersRoundsSet3 = Array.from({ length: 12 }, (_, i) => i + 97);        // 97-108
  const winnersRoundSet4 = Array.from({ length: 4 }, (_, i) => i + 109);        // 109-112
  const losersRoundsSet4 = Array.from({ length: 6 }, (_, i) => i + 113);        // 113-118
  const winnersRoundSet5 = Array.from({ length: 2 }, (_, i) => i + 119);        // 119-120
  const losersRoundsSet5 = Array.from({ length: 3 }, (_, i) => i + 121);        // 121-123

  delete siteData.bracketsModeSettings.gameSeedSetup.activeSeeds[
    "seed" + currentSeed
  ];

  // 1-32 (Winners)
  if (winnersRoundsSet1.includes(currentRound)) {
    brackets.winners.push(winner);
    brackets.losers.push(loser);
    if (currentRound === itemLast(winnersRoundsSet1, 1)) {
      siteData.bracketsModeSettings.currentSeed = 1;
      setSeeds64(siteData, brackets, "losers");
    } else {
      siteData.bracketsModeSettings.currentSeed += 1;
      siteData.bracketsModeSettings.current2Seeds =
        siteData.bracketsModeSettings.gameSeedSetup.activeSeeds[
          "seed" + (currentSeed + 1)
        ]!;
    }
  }

  // 33-48 (Losers)
  if (losersRoundsSet1.includes(currentRound)) {
    gameResults.push(loser);
    const indexOfLoser = brackets.losers.indexOf(loser);
    brackets.losers.splice(indexOfLoser, 1, -1);
    if (currentRound === itemLast(losersRoundsSet1, 1)) {
      siteData.bracketsModeSettings.currentSeed = 1;
      setSeeds64(siteData, brackets, "winners");
      const keepSecondHalf = brackets.losers.slice(16, 32);
      const flipFirstHalf = brackets.losers.slice(0, 16);
      brackets.losers = [...keepSecondHalf, ...flipFirstHalf];
    } else {
      siteData.bracketsModeSettings.currentSeed += 1;
      siteData.bracketsModeSettings.current2Seeds =
        siteData.bracketsModeSettings.gameSeedSetup.activeSeeds[
          "seed" + (currentSeed + 1)
        ]!;
    }
    if (!brackets.losers.includes(winner)) {
      brackets.losers.push(winner);
    }
  }

  // 49-64 (Winners)
  if (winnersRoundSet2.includes(currentRound)) {
    if (brackets.winners.includes(loser)) {
      const loserIndex = brackets.winners.indexOf(loser);
      brackets.winners.splice(loserIndex, 1);
    }
    const negativeValue = brackets.losers.indexOf(-1);
    brackets.losers.splice(negativeValue, 1, loser);
    if (currentRound === itemLast(winnersRoundSet2, 1)) {
      siteData.bracketsModeSettings.currentSeed = 1;
      setSeeds64(siteData, brackets, "losers");
    } else {
      siteData.bracketsModeSettings.currentSeed += 1;
      siteData.bracketsModeSettings.current2Seeds =
        siteData.bracketsModeSettings.gameSeedSetup.activeSeeds[
          "seed" + (currentSeed + 1)
        ]!;
    }
  }

  // 65-88 (Losers) — 24 rounds
  if (losersRoundsSet2.includes(currentRound)) {
    gameResults.push(loser);
    const indexOfLoser = brackets.losers.indexOf(loser);
    if (
      currentRound === itemLast(losersRoundsSet2, 1) ||
      currentRound === itemLast(losersRoundsSet2, 2) ||
      currentRound === itemLast(losersRoundsSet2, 3) ||
      currentRound === itemLast(losersRoundsSet2, 4) ||
      currentRound === itemLast(losersRoundsSet2, 5) ||
      currentRound === itemLast(losersRoundsSet2, 6) ||
      currentRound === itemLast(losersRoundsSet2, 7) ||
      currentRound === itemLast(losersRoundsSet2, 8)
    ) {
      brackets.losers.splice(indexOfLoser, 1, -1);
    } else {
      brackets.losers.splice(indexOfLoser, 1);
    }
    if (currentRound === itemLast(losersRoundsSet2, 9)) {
      siteData.bracketsModeSettings.currentSeed = 1;
      setSeeds64(siteData, brackets, "losers");
    }
    if (currentRound === itemLast(losersRoundsSet2, 1)) {
      siteData.bracketsModeSettings.currentSeed = 1;
      setSeeds64(siteData, brackets, "winners");
      const keepSecondHalf = brackets.losers.slice(8, 16);
      const flipFirstHalf = brackets.losers.slice(0, 8);
      brackets.losers = [...keepSecondHalf, ...flipFirstHalf];
    } else if (currentRound !== itemLast(losersRoundsSet2, 9)) {
      siteData.bracketsModeSettings.currentSeed += 1;
      siteData.bracketsModeSettings.current2Seeds =
        siteData.bracketsModeSettings.gameSeedSetup.activeSeeds[
          "seed" + (currentSeed + 1)
        ]!;
    }
    if (!brackets.losers.includes(winner)) {
      brackets.losers.push(winner);
    }
  }

  // 89-96 (Winners)
  if (winnersRoundSet3.includes(currentRound)) {
    if (brackets.winners.includes(loser)) {
      const loserIndex = brackets.winners.indexOf(loser);
      brackets.winners.splice(loserIndex, 1);
    }
    const negativeValue = brackets.losers.indexOf(-1);
    brackets.losers.splice(negativeValue, 1, loser);
    if (currentRound === itemLast(winnersRoundSet3, 1)) {
      siteData.bracketsModeSettings.currentSeed = 1;
      setSeeds64(siteData, brackets, "losers");
    } else {
      siteData.bracketsModeSettings.currentSeed += 1;
      siteData.bracketsModeSettings.current2Seeds =
        siteData.bracketsModeSettings.gameSeedSetup.activeSeeds[
          "seed" + (currentSeed + 1)
        ]!;
    }
  }

  // 97-108 (Losers) — 12 rounds
  if (losersRoundsSet3.includes(currentRound)) {
    gameResults.push(loser);
    const indexOfLoser = brackets.losers.indexOf(loser);
    if (
      currentRound === itemLast(losersRoundsSet3, 1) ||
      currentRound === itemLast(losersRoundsSet3, 2) ||
      currentRound === itemLast(losersRoundsSet3, 3) ||
      currentRound === itemLast(losersRoundsSet3, 4)
    ) {
      brackets.losers.splice(indexOfLoser, 1, -1);
    } else {
      brackets.losers.splice(indexOfLoser, 1);
    }
    if (currentRound === itemLast(losersRoundsSet3, 5)) {
      siteData.bracketsModeSettings.currentSeed = 1;
      setSeeds64(siteData, brackets, "losers");
    }
    if (currentRound === itemLast(losersRoundsSet3, 1)) {
      siteData.bracketsModeSettings.currentSeed = 1;
      setSeeds64(siteData, brackets, "winners");
      const keepSecondHalf = brackets.losers.slice(4, 8);
      const flipFirstHalf = brackets.losers.slice(0, 4);
      brackets.losers = [...keepSecondHalf, ...flipFirstHalf];
    } else if (currentRound !== itemLast(losersRoundsSet3, 5)) {
      siteData.bracketsModeSettings.currentSeed += 1;
      siteData.bracketsModeSettings.current2Seeds =
        siteData.bracketsModeSettings.gameSeedSetup.activeSeeds[
          "seed" + (currentSeed + 1)
        ]!;
    }
    if (!brackets.losers.includes(winner)) {
      brackets.losers.push(winner);
    }
  }

  // 109-112 (Winners)
  if (winnersRoundSet4.includes(currentRound)) {
    if (brackets.winners.includes(loser)) {
      const loserIndex = brackets.winners.indexOf(loser);
      brackets.winners.splice(loserIndex, 1);
    }
    const negativeValue = brackets.losers.indexOf(-1);
    brackets.losers.splice(negativeValue, 1, loser);
    if (currentRound === itemLast(winnersRoundSet4, 1)) {
      siteData.bracketsModeSettings.currentSeed = 1;
      setSeeds64(siteData, brackets, "losers");
    } else {
      siteData.bracketsModeSettings.currentSeed += 1;
      siteData.bracketsModeSettings.current2Seeds =
        siteData.bracketsModeSettings.gameSeedSetup.activeSeeds[
          "seed" + (currentSeed + 1)
        ]!;
    }
  }

  // 113-118 (Losers) — 6 rounds
  if (losersRoundsSet4.includes(currentRound)) {
    gameResults.push(loser);
    const indexOfLoser = brackets.losers.indexOf(loser);
    if (
      currentRound === itemLast(losersRoundsSet4, 1) ||
      currentRound === itemLast(losersRoundsSet4, 2)
    ) {
      brackets.losers.splice(indexOfLoser, 1, -1);
    } else {
      brackets.losers.splice(indexOfLoser, 1);
    }
    if (currentRound === itemLast(losersRoundsSet4, 3)) {
      siteData.bracketsModeSettings.currentSeed = 1;
      setSeeds64(siteData, brackets, "losers");
    }
    if (currentRound === itemLast(losersRoundsSet4, 1)) {
      siteData.bracketsModeSettings.currentSeed = 1;
      setSeeds64(siteData, brackets, "winners");
      brackets.losers = flipArray(brackets.losers);
    } else if (currentRound !== itemLast(losersRoundsSet4, 3)) {
      siteData.bracketsModeSettings.currentSeed += 1;
      siteData.bracketsModeSettings.current2Seeds =
        siteData.bracketsModeSettings.gameSeedSetup.activeSeeds[
          "seed" + (currentSeed + 1)
        ]!;
    }
    if (!brackets.losers.includes(winner)) {
      brackets.losers.push(winner);
    }
  }

  // 119-120 (Winners)
  if (winnersRoundSet5.includes(currentRound)) {
    if (brackets.winners.includes(loser)) {
      const loserIndex = brackets.winners.indexOf(loser);
      brackets.winners.splice(loserIndex, 1);
    }
    const negativeValue = brackets.losers.indexOf(-1);
    brackets.losers.splice(negativeValue, 1, loser);
    if (currentRound === itemLast(winnersRoundSet5, 1)) {
      siteData.bracketsModeSettings.currentSeed = 1;
      setSeeds64(siteData, brackets, "losers");
    } else {
      siteData.bracketsModeSettings.currentSeed += 1;
      siteData.bracketsModeSettings.current2Seeds =
        siteData.bracketsModeSettings.gameSeedSetup.activeSeeds[
          "seed" + (currentSeed + 1)
        ]!;
    }
  }

  // 121-123 (Losers) — 3 rounds
  if (losersRoundsSet5.includes(currentRound)) {
    gameResults.push(loser);
    const indexOfLoser = brackets.losers.indexOf(loser);
    if (currentRound !== itemLast(losersRoundsSet5, 1)) {
      brackets.losers.splice(indexOfLoser, 1);
    } else {
      brackets.losers.splice(indexOfLoser, 1, -1);
    }
    if (currentRound === itemLast(losersRoundsSet5, 2)) {
      siteData.bracketsModeSettings.currentSeed = 1;
      setSeeds64(siteData, brackets, "losers");
    }
    if (currentRound === itemLast(losersRoundsSet5, 1)) {
      siteData.bracketsModeSettings.currentSeed = 1;
      setSeeds64(siteData, brackets, "winners");
    } else if (currentRound !== itemLast(losersRoundsSet5, 2)) {
      siteData.bracketsModeSettings.currentSeed += 1;
      siteData.bracketsModeSettings.current2Seeds =
        siteData.bracketsModeSettings.gameSeedSetup.activeSeeds[
          "seed" + (currentSeed + 1)
        ]!;
    }
    if (!brackets.losers.includes(winner)) {
      brackets.losers.push(winner);
    }
  }

  // 124 (Winners — Semifinal)
  if (currentRound === 124) {
    const indexOfLoserInWinners = brackets.winners.indexOf(loser);
    brackets.winners.splice(indexOfLoserInWinners, 1);
    const indexOfNeg = brackets.losers.indexOf(-1);
    brackets.losers.splice(indexOfNeg, 1, loser);
    siteData.bracketsModeSettings.currentSeed = 1;
    setSeeds64(siteData, brackets, "losers");
    if (!brackets.winners.includes(winner)) {
      brackets.winners.push(winner);
    }
  }

  // 125 (Losers Final)
  if (currentRound === 125) {
    gameResults.push(loser);
    brackets.losers = [];
    brackets.winners.push(winner);
    siteData.bracketsModeSettings.currentSeed = 1;
    setSeeds64(siteData, brackets, "winners");
  }

  // 126 (Grand Final)
  if (currentRound === 126) {
    return { siteData, brackets, isFinal: true };
  }

  siteData.bracketsModeSettings.currentRound += 1;
  return { siteData, brackets, isFinal: false };
}
